// src/pages/taxonomy/Materials/MaterialsListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MaterialsAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';
const includesCi = (h = '', n = '') =>
  (h || '').toString().toLowerCase().includes((n || '').toString().toLowerCase());

export default function MaterialsListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters & pagination
  const [qName, setQName] = useState('');
  const [qCode, setQCode] = useState('');
  const [per, setPer] = useState(25);
  const [page, setPage] = useState(1);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await MaterialsAPI.list();
      const items = data?.items ?? data?.data ?? (Array.isArray(data) ? data : []);
      setRows(items || []);
    } catch (e) {
      setError(e.message || 'Failed to fetch materials');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // filter
  const filtered = useMemo(() => {
    let r = rows;
    if (qName) r = r.filter(x => includesCi(x.name, qName));
    if (qCode) r = r.filter(x => includesCi(x.code, qCode));
    return r;
  }, [rows, qName, qCode]);

  // paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(start, start + per), [filtered, start, per]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this material?')) return;
    try {
      await MaterialsAPI.destroy(id);
      const nextRows = rows.filter(r => r.id !== id);
      setRows(nextRows);
      const nextFiltered = nextRows.filter(x =>
        (!qName || includesCi(x.name, qName)) &&
        (!qCode || includesCi(x.code, qCode))
      );
      const nextTotalPages = Math.max(1, Math.ceil(nextFiltered.length / per));
      if (pageSafe > nextTotalPages) setPage(nextTotalPages);
    } catch (e) {
      alert(e.message);
    }
  }

  function resetFilters() {
    setQName('');
    setQCode('');
    setPage(1);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h3 className="mb-0">Materials</h3>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ width: 220 }}
            placeholder="Filter name…"
            value={qName}
            onChange={(e) => { setQName(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 180 }}
            placeholder="Filter code…"
            value={qCode}
            onChange={(e) => { setQCode(e.target.value); setPage(1); }}
          />
          <select
            className="form-select"
            style={{ width: 120 }}
            value={per}
            onChange={(e) => { setPer(parseInt(e.target.value, 10)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>
          <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
          <Link className="btn btn-primary" to={`${BASE}/materials/new`}>New</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 120 }}>Code</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length ? paged.map(row => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td className="text-monospace">{row.code}</td>
                    <td>
                      <Link className="btn btn-link p-0 me-3" to={`${BASE}/materials/${row.id}/edit`}>Edit</Link>
                      <button className="btn btn-link text-danger p-0" onClick={() => handleDelete(row.id)}>Delete</button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="text-muted">No data.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(1)}>&laquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>&lsaquo; Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next &rsaquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(totalPages)}>&raquo;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
